import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import { CHECKOUT_STEP_MAP, REVIEW_STEP, SHIP_OPTIONS_STEP } from 'constants/checkoutFlow';
import { SHIP_OPTION_GROUP_RETAIL_W_OPTIONS } from 'constants/shipOptionGroups';

import { trackEvent } from 'helpers/analytics';
import { fetchAddresses, receiveNewShippingField } from 'actions/checkout/addresses';
import { advanceCheckout, fetchCheckoutData, updateCheckoutForTargetStep } from 'actions/checkout/purchase';
import { receiveNewShipOptionsField } from 'actions/checkout/shipOptions';
import ContentBox from 'components/checkout/caap/ContentBox';
import PurchaseShippingOption from 'components/checkout/melody/shipOption/PurchaseShippingOption';
import ShippingOptionsModal from 'components/checkout/melody/shipOption/ShippingOptionsModal';
import SmallScreenOnly from 'components/checkout/caap/SmallScreenOnly';
import StepSection from 'components/checkout/caap/StepSection';
import { formatPrice } from 'containers/Checkout/ShipOptionsSection';
import { getPathFromStep } from 'helpers/CheckoutFlowControl';
import {
  isChangingPayment,
  isChangingShipOptions,
  isChangingShipping,
  isDigitalCart,
  isDigitalDeliveryOnlyCart
} from 'helpers/CheckoutUtils';

export class ShippingOptionsSection extends Component {
  onChangeShippingOptionsClick = () => {
    const {
      fetchAddresses,
      receiveNewShippingField
    } = this.props;

    trackEvent('TE_CHECKOUT_SPC_CHANGE_SHIPPING');
    fetchAddresses();
    receiveNewShippingField('isBilling', false);
  }

  onCloseLinkClick = e => {
    e.preventDefault();
    const { updateCheckoutForTargetStep } = this.props;
    trackEvent('TE_CHECKOUT_SPC_CLOSE_SEL_ADDRESS');
    updateCheckoutForTargetStep({ targetStep: REVIEW_STEP, newUrl: getPathFromStep(REVIEW_STEP) });
  }

  onShipOptionSelected = shipOptionId => {
    const { receiveNewShipOptionsField } = this.props;
    receiveNewShipOptionsField('shipOptionId', shipOptionId);
  }

  onShipOptionSelectedDesktop = shipOptionId => {
    const { receiveNewShipOptionsField } = this.props;
    receiveNewShipOptionsField('shipOptionId', shipOptionId);
  }

  onUseShipOptionClick = shipIdToUse => {
    const {
      advanceCheckout,
      checkoutData,
      fetchCheckoutData
    } = this.props;

    const {
      purchaseData: {
        data: { promise },
        isFetchingCheckoutData
      },
      shipOptions: { saved: savedShipOptions, new: newShipOption = {} }
    } = checkoutData;

    const { shipOptionId: selectedShipOptionId } = newShipOption;

    trackEvent('TE_CHECKOUT_SO_USE_OPTION');

    if (!isFetchingCheckoutData) {
      const currentShipOption = (savedShipOptions || []).find(
        item => item.promise === promise) || { id: null };

      if (typeof shipIdToUse === 'string') {
        if (shipIdToUse !== currentShipOption.id) {
          fetchCheckoutData({ shipmentOptionId: shipIdToUse, showFullPageSpinner: false, advanceOnSuccess: true });
        } else {
          advanceCheckout();
        }
      } else {
        if (selectedShipOptionId && selectedShipOptionId !== currentShipOption.id) {
          fetchCheckoutData({ shipmentOptionId: selectedShipOptionId, showFullPageSpinner: false, advanceOnSuccess: true });
        } else {
          advanceCheckout();
        }
      }
    }
  }

  showShippingOptionsStep = () => {
    const { targetStep } = this.props.checkoutData.purchaseData;
    return targetStep === SHIP_OPTIONS_STEP || targetStep === REVIEW_STEP;
  }

  render() {
    const {
      purchaseData: {
        cartType,
        data: { purchaseId }
      }
    } = this.props.checkoutData;

    const {
      shipOptions,
      purchaseData: {
        data: {
          chargeSummary: { shippingCharge },
          deliveryPromise,
          promise
        },
        isFetchingCheckoutData,
        purchaseShipOptionId,
        targetStep
      }
    } = this.props.checkoutData;

    const {
      groups,
      new: newShipOption
    } = shipOptions;

    const shipOptionsForModal = groups[SHIP_OPTION_GROUP_RETAIL_W_OPTIONS] || { deliveryOptions: [] };
    const hasDigitalDelivery = isDigitalCart(cartType);
    const hasRetailDelivery = groups.hasOwnProperty(SHIP_OPTION_GROUP_RETAIL_W_OPTIONS);
    const isDigitalDeliveryOnly = isDigitalDeliveryOnlyCart(cartType);
    const showShippingOptionsStep = this.showShippingOptionsStep();
    const isChangeShipping = isChangingShipping(targetStep);
    const isChangePayment = isChangingPayment(targetStep);
    const isChangeShippingOptions = isChangingShipOptions(targetStep);
    const showChangeLink = !isChangeShipping && !isChangePayment;
    const isSelectShipOptionStep = targetStep === SHIP_OPTIONS_STEP;
    const selectedShipOptionId = (newShipOption && newShipOption.shipOptionId)
    || purchaseShipOptionId
    || null;

    const { testId = f => f } = this.context;

    const retailContent = <div data-test-id={testId('mixedDelivery')}>
      <SmallScreenOnly><h2>Shipping Options</h2></SmallScreenOnly>
      <ContentBox
        isSolo={true}
        isNonRadio={true}>
        <PurchaseShippingOption
          hasDigitalDelivery={hasDigitalDelivery}
          hasRetailDelivery={hasRetailDelivery}
          deliveryPromise={deliveryPromise}
          promise={promise}
          shippingCharge={shippingCharge}
          onChangeAddressClick={this.onChangeShippingOptionsClick}
          purchaseId={purchaseId}
          showChangeLink={showChangeLink} />
      </ContentBox>
    </div>;

    if (isSelectShipOptionStep) {
      return (
        <StepSection
          addOverlay={false}
          linkTo={`${CHECKOUT_STEP_MAP[SHIP_OPTIONS_STEP]}?referrer=spc&pid=${purchaseId}`}
          linkText="Close"
          isCurrentStep={showShippingOptionsStep}
          onLinkClick={this.onCloseLinkClick}
          showLink={isChangeShippingOptions}
          showTitleBar={isChangeShippingOptions}
          stepNumber="3"
          title="Choose shipping options"
          dataTestIdLoader={`${testId('spinner')}`}>
          <ShippingOptionsModal
            isFetchingCheckoutData={isFetchingCheckoutData}
            formatPrice={formatPrice}
            onShipOptionSelected={this.onShipOptionSelectedDesktop}
            onUseShipOptionClick={this.onUseShipOptionClick}
            sectionKey="desktop"
            selectedShipOptionId={selectedShipOptionId}
            shipPromise={promise}
            shipOptions={shipOptionsForModal.deliveryOptions}
          />
        </StepSection>
      );
    }

    if (isDigitalDeliveryOnly) {
      return null;
    }

    return (
      <StepSection
        addOverlay={false}
        linkTo={`${CHECKOUT_STEP_MAP[SHIP_OPTIONS_STEP]}?referrer=spc&pid=${purchaseId}`}
        linkText="Close"
        isCurrentStep={showShippingOptionsStep}
        onLinkClick={this.onCloseLinkClick}
        showLink={showChangeLink}
        showTitleBar={false}
        stepNumber="3"
        title="Choose shipping options"
        dataTestIdLoader={`${testId('spinner')}`}>
        {
          retailContent
        }
      </StepSection>
    );
  }
}

function mapStateToProps(state) {
  return {
    checkoutData: state.checkoutData,
    location: state.routing.locationBeforeTransitions
  };
}

ShippingOptionsSection.contextTypes = {
  testId: PropTypes.func
};

export default connect(mapStateToProps, {
  advanceCheckout,
  fetchAddresses,
  fetchCheckoutData,
  receiveNewShippingField,
  updateCheckoutForTargetStep,
  receiveNewShipOptionsField
})(ShippingOptionsSection);
